import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Building2, Users, ArrowRight, Check } from 'lucide-react';

// Warm Premium Palette - Matching site theme
const colors = {
  primaryTeal: '#3D6B67',
  primaryTealLight: '#4D8B86',
  primaryBrown: '#5C4033',
  primaryBrownDark: '#4A3328',
  bgPage: '#FAF6F2',
  bgSage: '#DCE7E3',
  bgCard: '#FFFFFF',
  textOnDark: '#FFFFFF',
  textSecondary: '#5D4E47',
  textTertiary: '#8B7D76',
  textMain: '#2C1810',
  borderWarm: '#E8E0D8',
  success: '#4D9078',
};

const XchangeRegister = () => {
  const navigate = useNavigate();
  const { role } = useParams();
  const [formData, setFormData] = useState({
    role: role || 'corporate', orgName: '', contactName: '', email: '', phone: '',
    sectors: [], states: [], budgetMin: '', budgetMax: '', description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sectorOptions = ['Education', 'Healthcare', 'Skilling', 'Women Empowerment', 'Environment', 'Livelihood', 'Rural Development', 'Child Welfare', 'Sanitation', 'Water'];
  const stateOptions = ['Andhra Pradesh', 'Bihar', 'Delhi', 'Gujarat', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Punjab', 'Rajasthan', 'Tamil Nadu', 'Telangana', 'Uttar Pradesh', 'West Bengal'];

  const toggleArrayValue = (field, value) => {
    const current = formData[field];
    if (current.includes(value)) setFormData({ ...formData, [field]: current.filter(v => v !== value) });
    else setFormData({ ...formData, [field]: [...current, value] });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/xchange/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, status: 'pending' }),
      });
      if (response.ok) {
        alert('Registration submitted! Admin will review your listing.');
        navigate('/xchange');
      } else {
        const data = await response.json();
        setError(data.detail || 'Submission failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isCorporate = formData.role === 'corporate';

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.bgPage }}>
      <Header />
      
      <div className="pt-28 pb-16 px-6">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div 
              className="w-16 h-16 rounded-[14px] mx-auto mb-5 flex items-center justify-center"
              style={{ backgroundColor: colors.bgSage }}
            >
              {isCorporate ? 
                <Building2 className="w-7 h-7" style={{ color: colors.primaryTeal }} /> : 
                <Users className="w-7 h-7" style={{ color: colors.primaryTeal }} />
              }
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: colors.textMain }}>
              {isCorporate ? 'Register as Sponsor' : 'Register as NGO'}
            </h1>
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              {isCorporate ? 'Join as a corporate CSR sponsor' : 'Register your implementing organization'}
            </p>
          </div>

          {/* Role Toggle */}
          <div 
            className="flex rounded-[12px] p-1 mb-8"
            style={{ backgroundColor: colors.bgSage }}
          >
            <button
              onClick={() => setFormData({ ...formData, role: 'corporate' })}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-[10px] text-sm font-medium transition-all duration-200"
              style={{ 
                backgroundColor: isCorporate ? colors.primaryTeal : 'transparent',
                color: isCorporate ? colors.textOnDark : colors.textMain
              }}
            >
              <Building2 className="w-4 h-4" />
              Sponsor
            </button>
            <button
              onClick={() => setFormData({ ...formData, role: 'ngo' })}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-[10px] text-sm font-medium transition-all duration-200"
              style={{ 
                backgroundColor: !isCorporate ? colors.primaryTeal : 'transparent',
                color: !isCorporate ? colors.textOnDark : colors.textMain
              }}
            >
              <Users className="w-4 h-4" />
              NGO
            </button>
          </div>

          {/* Form */}
          <div 
            className="rounded-[20px] p-8"
            style={{ 
              backgroundColor: colors.bgCard,
              boxShadow: '0 10px 40px rgba(44, 24, 16, 0.08)',
              border: `1px solid ${colors.borderWarm}`
            }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.textMain }}>
                    {isCorporate ? 'Company Name *' : 'Organization Name *'}
                  </label>
                  <input
                    type="text"
                    value={formData.orgName}
                    onChange={(e) => setFormData({ ...formData, orgName: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-[10px] outline-none transition-all duration-200"
                    style={{ backgroundColor: colors.bgSage, color: colors.textMain, border: '1px solid transparent' }}
                    onFocus={(e) => e.currentTarget.style.borderColor = colors.primaryTeal}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'transparent'}
                    placeholder={isCorporate ? 'Acme Corp Pvt Ltd' : 'Hope Foundation'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.textMain }}>Contact Person *</label>
                  <input
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-[10px] outline-none transition-all duration-200"
                    style={{ backgroundColor: colors.bgSage, color: colors.textMain, border: '1px solid transparent' }}
                    onFocus={(e) => e.currentTarget.style.borderColor = colors.primaryTeal}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'transparent'}
                    placeholder="Full Name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.textMain }}>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-[10px] outline-none transition-all duration-200"
                    style={{ backgroundColor: colors.bgSage, color: colors.textMain, border: '1px solid transparent' }}
                    onFocus={(e) => e.currentTarget.style.borderColor = colors.primaryTeal}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'transparent'}
                    placeholder="email@company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: colors.textMain }}>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-[10px] outline-none transition-all duration-200"
                    style={{ backgroundColor: colors.bgSage, color: colors.textMain, border: '1px solid transparent' }}
                    onFocus={(e) => e.currentTarget.style.borderColor = colors.primaryTeal}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'transparent'}
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-3" style={{ color: colors.textMain }}>
                  {isCorporate ? 'CSR Focus Areas *' : 'Sectors of Work *'}
                </label>
                <div className="flex flex-wrap gap-2">
                  {sectorOptions.map((sector) => (
                    <button
                      key={sector}
                      type="button"
                      onClick={() => toggleArrayValue('sectors', sector)}
                      className="px-4 py-2 rounded-[10px] text-sm font-medium transition-all duration-200"
                      style={{ 
                        backgroundColor: formData.sectors.includes(sector) ? colors.primaryTeal : colors.bgSage,
                        color: formData.sectors.includes(sector) ? colors.textOnDark : colors.textMain
                      }}
                    >
                      {formData.sectors.includes(sector) && <Check className="w-4 h-4 inline mr-1" />}
                      {sector}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-3" style={{ color: colors.textMain }}>
                  {isCorporate ? 'Geographic Preference' : 'Operational States'}
                </label>
                <div className="flex flex-wrap gap-2">
                  {stateOptions.map((state) => (
                    <button
                      key={state}
                      type="button"
                      onClick={() => toggleArrayValue('states', state)}
                      className="px-4 py-2 rounded-[10px] text-sm font-medium transition-all duration-200"
                      style={{ 
                        backgroundColor: formData.states.includes(state) ? colors.primaryTeal : colors.bgSage,
                        color: formData.states.includes(state) ? colors.textOnDark : colors.textMain
                      }}
                    >
                      {state}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: colors.textMain }}>
                  {isCorporate ? 'About Your CSR Goals' : 'About Your Organization'}
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 rounded-[10px] outline-none resize-none transition-all duration-200"
                  style={{ backgroundColor: colors.bgSage, color: colors.textMain, border: '1px solid transparent' }}
                  onFocus={(e) => e.currentTarget.style.borderColor = colors.primaryTeal}
                  onBlur={(e) => e.currentTarget.style.borderColor = 'transparent'}
                  placeholder={isCorporate ? 'Tell us about your CSR vision and objectives...' : 'Describe your organization, mission, and key programs...'}
                />
              </div>

              {error && (
                <div className="p-4 rounded-[10px] text-sm" style={{ backgroundColor: 'rgba(220, 38, 38, 0.1)', color: '#dc2626' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-[12px] font-semibold transition-all duration-300 disabled:opacity-50"
                style={{ 
                  backgroundColor: colors.primaryTeal, 
                  color: colors.textOnDark,
                  boxShadow: '0 4px 14px rgba(61, 107, 103, 0.3)'
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = colors.primaryTealLight; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = colors.primaryTeal; }}
              >
                {loading ? 'Submitting...' : 'Submit Registration'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>
          </div>

          {/* Login Link */}
          <div className="text-center mt-6">
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              Already registered?{' '}
              <button 
                onClick={() => navigate('/xchange/login')}
                className="font-medium transition-colors"
                style={{ color: colors.primaryTeal }}
                onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryBrown}
                onMouseLeave={(e) => e.currentTarget.style.color = colors.primaryTeal}
              >
                Sign in here
              </button>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default XchangeRegister;
